import React, { useMemo, useRef, useState, useEffect } from "react";
import { createPortal } from "react-dom";
import InputField from "../components/InputField";
import SelectField from "../components/SelectField";
import RadioGroup from "../components/RadioGroup";
import Button from "../components/Button";
import {
  registrarPostulante,
  inicializarFormulario,
  getUbigeo,
} from "../services/postulanteService";
import "./styles/FormPostulante.css";
import {
  getAllowedBirthDateRange,
  isBirthDateInAllowedRange,
  isoToDisplayDate,
  normalizeFechaInput,
  parseFechaNacimiento,
  toDisplayDate,
  toIsoDate,
} from "../utils";
import {
  estadoCivilMap,
  generateDefaultValues,
  generateFormReadOnly,
  nacionalidadMap,
  tipoDocumentoMap,
} from "../helpers/FormPostulante";
import { REACT_APP_CAPTCHA_KEY } from "../config/environment";
import { Turnstile } from "@marsidev/react-turnstile";

const FormPostulante = () => {
  const calendarInputRef = useRef(null);
  const [formData, setFormData] = useState(generateDefaultValues());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [isFormLocked, setIsFormLocked] = useState(false);
  const [catalogos, setCatalogos] = useState(null);
  const [ubigeo, setUbigeo] = useState([]);
  const [errors, setErrors] = useState({});
  const [turnstileToken, setTurnstileToken] = useState(null);

  useEffect(() => {
    inicializarFormulario().then((res) => {
      setCatalogos(res);
    });
    getUbigeo().then((res) => {
      setUbigeo(res.items);
    });
  }, []);

  const departamentos = useMemo(() => {
    return [
      ...new Map(
        ubigeo.map((i) => [
          i.codigoDepartamento,
          { value: i.codigoDepartamento, label: i.nombreDepartamento },
        ])
      ).values(),
    ].sort((a, b) => a.label.localeCompare(b.label));
  }, [ubigeo]);
  const provincias = useMemo(() => {
    if (!formData.departamento) return [];
    return [
      ...new Map(
        ubigeo
          .filter((i) => i.codigoDepartamento === formData.departamento)
          .map((i) => [
            i.codigoProvincia,
            { value: i.codigoProvincia, label: i.nombreProvincia },
          ])
      ).values(),
    ].sort((a, b) => a.label.localeCompare(b.label));
  }, [formData.departamento, ubigeo]);
  const distritos = useMemo(() => {
    if (!formData.departamento || !formData.provincia) return [];
    return [
      ...new Map(
        ubigeo
          .filter(
            (i) =>
              i.codigoDepartamento === formData.departamento &&
              i.codigoProvincia === formData.provincia
          )
          .map((i) => [
            i.codigoDistrito,
            { value: i.codigoDistrito, label: i.nombreDistrito },
          ])
      ).values(),
    ].sort((a, b) => a.label.localeCompare(b.label));
  }, [formData.departamento, formData.provincia, ubigeo]);

  useEffect(() => {
    if (!feedback) return;
    const closeDelayMs = feedback.type === "success" ? 4200 : 5200;
    const timerId = window.setTimeout(() => {
      setFeedback(null);
    }, closeDelayMs);
    return () => {
      window.clearTimeout(timerId);
    };
  }, [feedback]);

  const ubigeoSeleccionado = useMemo(() => {
    if (!formData.departamento || !formData.provincia || !formData.distrito) {
      return null;
    }
    return ubigeo.find(
      (u) =>
        u.codigoDepartamento === formData.departamento &&
        u.codigoProvincia === formData.provincia &&
        u.codigoDistrito === formData.distrito
    );
  }, [formData.departamento, formData.provincia, formData.distrito, ubigeo]);

  const { maxDate: maxBirthDate } = getAllowedBirthDateRange();
  const parsedBirthDate = parseFechaNacimiento(formData.fechaNacimiento);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "departamento") {
      setFormData({
        ...formData,
        departamento: value,
        provincia: "",
        distrito: "",
      });
      return;
    }
    if (name === "provincia") {
      setFormData({
        ...formData,
        provincia: value,
        distrito: "",
      });
      return;
    }
    if (name === "distrito") {
      setFormData({
        ...formData,
        distrito: value,
      });
      return;
    }
    setFormData({
      ...formData,
      [name]: value,
    });
  };
  const handleFechaNacimientoBlur = () => {
    setFormData({
      ...formData,
      fechaNacimiento: normalizeFechaInput(formData.fechaNacimiento),
    });
  };
  const handleCalendarOpen = () => {
    if (!calendarInputRef.current) return;
    if (typeof calendarInputRef.current.showPicker === "function") {
      calendarInputRef.current.showPicker();
      return;
    }
    calendarInputRef.current.focus();
    calendarInputRef.current.click();
  };

  const handleCalendarChange = (e) => {
    const displayValue = isoToDisplayDate(e.target.value);
    setFormData({ ...formData, fechaNacimiento: displayValue });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isFormLocked) {
      return;
    }
    setFeedback(null);
    setErrors({});
    const telefonoLength = formData.telefono.length;
    const telefonoReferenciaLength = formData.telefonoReferencia.length;
    if (
      telefonoLength < 7 ||
      telefonoLength > 9 ||
      telefonoReferenciaLength < 7 ||
      telefonoReferenciaLength > 9
    ) {
      setFeedback({
        type: "error",
        message: "Los campos de teléfono deben tener entre 7 y 9 números.",
      });
      return;
    }

    const birthDate = parseFechaNacimiento(formData.fechaNacimiento);
    if (!birthDate) {
      setFeedback({
        type: "error",
        message: "La fecha de nacimiento debe tener formato válido dd/mm/aaaa.",
      });
      return;
    }

    if (!isBirthDateInAllowedRange(birthDate)) {
      const { maxDate } = getAllowedBirthDateRange();
      setFeedback({
        type: "error",
        message: `La fecha de nacimiento no puede ser mayor a ${toDisplayDate(maxDate)}.`,
      });
      return;
    }
    //TURNSTILE: bloquear envío si no se validó el captcha
    setIsSubmitting(true);
    try {
      if (formData.nroHijos === "Otras") {
      if (
        !formData.nroHijosOtro ||
        formData.nroHijosOtro === ""
      ) {
        setIsSubmitting(false);

        setFeedback({
          type: "error",
          message: "Ingresa la cantidad de hijos.",
        });

        return;
      }
    }
      const payload = {
        codigoTipoDocumento: tipoDocumentoMap[formData.tipoDocumento],
        numeroDocumento: formData.dni,
        codigoNacionalidad: nacionalidadMap[formData.nacionalidad],
        apellidoPaterno: formData.apellidoPaterno,
        apellidoMaterno: formData.apellidoMaterno,
        primerNombre: formData.primerNombre,
        segundoNombre: formData.segundoNombre,
        sexo: formData.sexo.toUpperCase() === "MASCULINO" ? 2 : 1,
        fechaNacimiento: toIsoDate(birthDate),
        direccion: formData.direccion,
        referenciaDomiciliaria: formData.referencia,
        codigoDepartamento: ubigeoSeleccionado?.codigoDepartamento,
        codigoProvincia: ubigeoSeleccionado?.codigoProvincia,
        codigoDistrito: ubigeoSeleccionado?.codigoDistrito,
        telefonoCelular: formData.telefono,
        telefonoReferencia: formData.telefonoReferencia,
        correoElectronico: formData.correo,

        numeroHijos:
          formData.nroHijos === "Otras"
            ? Number(formData.nroHijosOtro)
            : formData.nroHijos,
            
        nombrePuesto: formData.puesto,
        codigoEstadoCivil: estadoCivilMap[formData.estadoCivil],
        nombreReclutador: formData.reclutador,
        fuente: formData.fuente,
        experiencia: formData.experiencia,
        autorizacionDatos: formData.autorizacion === "SI",
        //TURNSTILE: enviar token al backend
        token: turnstileToken,
      };

      const respuesta = await registrarPostulante(payload);
      if (respuesta?.exito) {
        setFeedback({
          type: "success",
          message:
            "Formulario enviado correctamente. Tu postulación fue registrada.",
        });
        setIsFormLocked(true);
      } else {
        //TURNSTILE: resetear token si backend falla
        // setTurnstileToken(null);
        setFeedback({
          type: "error",
          message:
            respuesta?.mensajeError || "No se pudo registrar la postulación.",
        });
      }
    } catch (error) {
      // setTurnstileToken(null);
      setFeedback({
        type: "error",
        message:
          error?.response?.data?.mensaje ||
          error?.response?.data?.message ||
          "No se pudo enviar la postulación. Inténtalo nuevamente.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {isFormLocked ? (
        <div className="space-y-5">
          <header>
            <h2 className="text-2xl font-semibold text-brand-ink">
              Ficha de Postulante
            </h2>
            <p className="mt-1 text-sm text-brand-subtle">
              Tu postulación fue registrada correctamente.
            </p>
          </header>

          {generateFormReadOnly(
            formData,
            departamentos,
            provincias,
            distritos
          ).map((section) => (
            <section key={section.title} className="section-card animate-rise">
              <h3 className="section-title">{section.title}</h3>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {section.fields.map((field) => (
                  <div key={field.label}>
                    <p className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-brand-muted">
                      {field.label}
                    </p>
                    <p className="rounded-xl border border-brand-line bg-brand-soft/30 px-3 py-2.5 text-sm text-brand-ink">
                      {field.value}
                    </p>
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-5">
          <header>
            <h2 className="text-2xl font-semibold text-brand-ink">
              Ficha de Postulante
            </h2>
            <p className="mt-1 text-sm text-brand-subtle">
              Ingresa información verídica para agilizar la evaluación de tu
              perfil.
            </p>
          </header>
          <section
            className="section-card animate-rise"
            style={{ animationDelay: "160ms" }}
          >
            <h3 className="section-title">Datos personales</h3>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <InputField
                label="DNI"
                name="dni"
                type="number"
                value={formData.dni}
                onChange={handleChange}
                placeholder="Ingresa tu número de documento"
                inputClassName="no-spinner"
                required
              />
              <SelectField
                label="Tipo de Documento"
                name="tipoDocumento"
                value={formData.tipoDocumento}
                onChange={handleChange}
                placeholder="Selecciona tipo de documento"
                options={
                  catalogos?.tiposDocumento
                    ?.map((x) => x.descripcion)
                    .filter(Boolean) || []
                }
                required
                error={errors.tipoDocumento}
              />
              <SelectField
                label="Nacionalidad"
                name="nacionalidad"
                value={formData.nacionalidad}
                onChange={handleChange}
                placeholder="Selecciona nacionalidad"
                options={
                  catalogos?.nacionalidades
                    ?.map((x) => x.descripcion)
                    .filter(Boolean) || []
                }
                searchable
                required
                error={errors.nacionalidad}
              />
              <InputField
                label="Apellido Paterno"
                name="apellidoPaterno"
                value={formData.apellidoPaterno}
                onChange={handleChange}
                placeholder="Ingresa tu apellido paterno"
                required
              />
              <InputField
                label="Apellido Materno"
                name="apellidoMaterno"
                value={formData.apellidoMaterno}
                onChange={handleChange}
                placeholder="Ingresa tu apellido materno"
                required
              />
              <InputField
                label="Primer Nombre"
                name="primerNombre"
                value={formData.primerNombre}
                onChange={handleChange}
                placeholder="Ingresa tu primer nombre"
                required
              />
              <InputField
                label="Segundo Nombre"
                name="segundoNombre"
                value={formData.segundoNombre}
                onChange={handleChange}
                placeholder="Ingresa tu segundo nombre"
              />
              <RadioGroup
                label="Sexo"
                name="sexo"
                options={
                  catalogos?.sexos?.map((x) => x.descripcion).filter(Boolean) ||
                  []
                }
                value={formData.sexo}
                onChange={handleChange}
                required
              />
              <div>
                <label
                  className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-brand-muted"
                  htmlFor="fechaNacimiento"
                >
                  Fecha de Nacimiento
                </label>
                <div className="relative">
                  <input
                    id="fechaNacimiento"
                    name="fechaNacimiento"
                    type="text"
                    value={formData.fechaNacimiento}
                    onChange={handleChange}
                    onBlur={handleFechaNacimientoBlur}
                    placeholder="dd/mm/aaaa"
                    inputMode="numeric"
                    maxLength={10}
                    pattern="\d{2}/\d{2}/\d{4}"
                    title="Ingresa una fecha válida en formato dd/mm/aaaa"
                    className="field-control w-full rounded-xl px-3 py-2.5 pr-10 text-sm text-brand-ink outline-none"
                    required
                  />
                  <button
                    type="button"
                    onClick={handleCalendarOpen}
                    className="absolute inset-y-0 right-0 flex w-10 items-center justify-center text-brand-subtle hover:text-brand-ink"
                    aria-label="Abrir calendario"
                  >
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      aria-hidden="true"
                      className="h-4 w-4"
                      stroke="currentColor"
                      strokeWidth="1.8"
                    >
                      <rect x="3.5" y="5.5" width="17" height="15" rx="2" />
                      <path d="M3.5 9.5h17" />
                      <path d="M8 3.5v4" />
                      <path d="M16 3.5v4" />
                    </svg>
                  </button>
                  <input
                    ref={calendarInputRef}
                    type="date"
                    value={parsedBirthDate ? toIsoDate(parsedBirthDate) : ""}
                    onChange={handleCalendarChange}
                    max={toIsoDate(maxBirthDate)}
                    className="absolute h-0 w-0 opacity-0"
                    tabIndex={-1}
                    aria-hidden="true"
                  />
                </div>
              </div>
            </div>
          </section>
          <section
            className="section-card animate-rise"
            style={{ animationDelay: "220ms" }}
          >
            <h3 className="section-title">Contacto y ubicación</h3>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <InputField
                label="Dirección"
                name="direccion"
                value={formData.direccion}
                onChange={handleChange}
                placeholder="Ingresa tu dirección completa"
                className="md:col-span-2"
                required
              />
              <InputField
                label="Referencia Domiciliaria"
                name="referencia"
                value={formData.referencia}
                onChange={handleChange}
                placeholder="Ejemplo: Frente al parque principal"
                className="md:col-span-2"
                required
              />
              <SelectField
                label="Departamento"
                name="departamento"
                value={formData.departamento}
                onChange={handleChange}
                placeholder="Selecciona departamento"
                options={departamentos}
                searchable
                required
                error={errors.departamento}
              />
              <SelectField
                label="Provincia"
                name="provincia"
                value={formData.provincia}
                onChange={handleChange}
                placeholder="Selecciona provincia"
                options={provincias}
                disabled={!formData.departamento}
                searchable
                required
                error={errors.provincia}
              />
              <SelectField
                label="Distrito"
                name="distrito"
                value={formData.distrito}
                onChange={handleChange}
                placeholder="Selecciona distrito"
                options={distritos}
                disabled={!formData.provincia}
                searchable
                required
                error={errors.distrito}
              />
              <InputField
                label="Teléfono Celular"
                name="telefono"
                type="text"
                value={formData.telefono}
                onChange={handleChange}
                placeholder="Ingresa tu teléfono celular"
                inputClassName="no-spinner"
                inputMode="numeric"
                minLength={7}
                maxLength={9}
                pattern="[0-9]{7,9}"
                title="Ingresa entre 7 y 9 números"
                required
              />
              <InputField
                label="Teléfono de Referencia"
                name="telefonoReferencia"
                type="text"
                value={formData.telefonoReferencia}
                onChange={handleChange}
                placeholder="Ingresa teléfono de referencia"
                inputClassName="no-spinner"
                inputMode="numeric"
                minLength={7}
                maxLength={9}
                pattern="[0-9]{7,9}"
                title="Ingresa entre 7 y 9 números"
                required
              />
              <InputField
                label="Correo Electrónico"
                name="correo"
                type="email"
                value={formData.correo}
                onChange={handleChange}
                placeholder="ejemplo@correo.com"
                className="md:col-span-2"
                required
              />
            </div>
          </section>
          <section
            className="section-card animate-rise"
            style={{ animationDelay: "280ms" }}
          >
            <h3 className="section-title">Perfil y evaluación</h3>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <RadioGroup
                label="N° Hijos"
                name="nroHijos"
                options={["0", "1", "2", "3", "Otras"]}
                value={formData.nroHijos}
                onChange={handleChange}
                required
              />
              {formData.nroHijos === "Otras" && (
                <InputField
                  label="Indica N° de Hijos"
                  name="nroHijosOtro"
                  type="number"
                  value={formData.nroHijosOtro || ""}
                  onChange={handleChange}
                  placeholder="Ingresa cantidad de hijos"
                  inputClassName="no-spinner"
                  inputMode="numeric"
                  min={0}
                  step={1}
                  required
                />
              )}
              <SelectField
                label="¿A qué puesto postulas?"
                name="puesto"
                value={formData.puesto}
                onChange={handleChange}
                placeholder="Selecciona el puesto"
                options={
                  catalogos?.nivelPuesto
                    ?.map((x) => x.descripcion)
                    .filter(Boolean) || []
                }
                searchable
                required
                error={errors.puesto}
              />
              <SelectField
                label="Estado Civil"
                name="estadoCivil"
                value={formData.estadoCivil}
                onChange={handleChange}
                placeholder="Selecciona estado civil"
                options={
                  catalogos?.estadosCiviles
                    ?.map((x) => x.descripcion)
                    .filter(Boolean) || []
                }
                required
                error={errors.estadoCivil}
              />
              <SelectField
                label="Reclutador"
                name="reclutador"
                value={formData.reclutador}
                onChange={handleChange}
                options={
                  catalogos?.reclutadores?.map((x) => x.nombreCompleto) || []
                }
                placeholder="Selecciona reclutador"
                className="md:col-span-2"
                searchable
                required
              />
              <SelectField
                label="Fuente"
                name="fuente"
                value={formData.fuente}
                onChange={handleChange}
                options={
                  catalogos?.fuentesTrabajo
                    ?.map((x) => x.descripcion)
                    .filter(Boolean) || []
                }
                placeholder="Selecciona fuente"
                required
                error={errors.fuente}
              />
              <SelectField
                label="Experiencia"
                name="experiencia"
                value={formData.experiencia}
                onChange={handleChange}
                options={
                  catalogos?.experiencias?.filter((x)=>  {
                  return [1,2,3,4,5,6].includes(x.codigo)
                  }).map((x) => {
                    return { value: x.codigo, label: x.descripcion };
                  }) || []
                }
                placeholder="Selecciona experiencia"
                required
                error={errors.experiencia}
              />
              <RadioGroup
                label="Autorización de datos"
                name="autorizacion"
                options={["SI", "NO"]}
                value={formData.autorizacion}
                onChange={handleChange}
                className="md:col-span-2"
                required
              />
              <p className="md:col-span-2 rounded-xl border border-brand-line bg-brand-soft/40 px-4 py-3 text-xs leading-5 text-brand-subtle">
                LOS DATOS PERSONALES SE ENTENDERAN COMO ACTUALES. SUSCRIBO Y
                REGISTRO MIS DATOS PERSONALES EN SENAL DE ACEPTACION Y
                AUTORIZACION PARA EL TRATAMIENTO DE LOS MISMOS, SEGUN LA LEY N°
                29773, LEY DE PROTECCION DE DATOS PERSONALES.
              </p>
            </div>
          </section>
          <div className="animate-rise mb-12 flex justify-center">
            <Turnstile
              siteKey={REACT_APP_CAPTCHA_KEY}
              onSuccess={(token) => setTurnstileToken(token)}
              onExpire={() => setTurnstileToken(null)}
              onError={() => setTurnstileToken(null)}
              appearance="interaction-only"
            />
          </div>
          <div className="animate-rise" style={{ animationDelay: "330ms" }}>
            <Button
              text={isSubmitting ? "Enviando..." : "Enviar"}
              type="submit"
              disabled={isSubmitting || !turnstileToken}
              className={`${turnstileToken ? "" : "disabled"} btnSubmit w-full btn_submit`}
            />
          </div>
        </form>
      )}
      {feedback &&
        createPortal(
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ backgroundColor: "#f6f2f2" }}
          >
            <div
              className="w-full max-w-md overflow-hidden rounded-2xl border px-6 py-7 text-center shadow-2xl"
              style={{ backgroundColor: "#ffffff", borderColor: "#d9b4b4" }}
            >
              <div
                className={`mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full ${feedback.type === "success" ? "text-brand-deep" : "text-brand-ink"}`}
                style={{
                  backgroundColor:
                    feedback.type === "success" ? "#f7e5e5" : "#fde8e8",
                }}
              >
                {feedback.type === "success" ? "✓" : "!"}
              </div>
              <h4
                className={`text-xl font-bold ${feedback.type === "success" ? "text-brand-deep" : "text-brand-ink"}`}
              >
                {feedback.type === "success"
                  ? "Postulación enviada"
                  : "Ocurrió un problema"}
              </h4>
              <p className="mt-3 text-sm leading-6 text-brand-subtle">
                {feedback.message}
              </p>
              <p className="mt-2 text-xs font-medium text-brand-muted">
                Este mensaje se cerrará automáticamente en unos segundos.
              </p>
              <div className="mt-6 flex justify-center">
                <button
                  type="button"
                  onClick={() => setFeedback(null)}
                  className={`rounded-lg px-5 py-2 text-sm font-semibold text-white transition ${feedback.type === "success" ? "bg-brand-deep hover:bg-[#560704]" : "bg-[#a34545] hover:bg-[#8f3434]"}`}
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
};
export default FormPostulante;
